import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from 'oidc-react';
import { StorageContext } from './StorageContext';
import { ApiFetchParams, useApiQuery } from '../helpers/useApi';
import CalendarPreferences from '../models/calendar';
import { DEFAULT_BUSINESS_START_HOUR } from '../constants';

const defaultSettings: Record<string, string> = {
    view: "hospitality",
    rowsPerPage: "10",
};

const SettingsContext = React.createContext({
    settings: defaultSettings,
    setSetting: (key: string, value: string) => { /* no op */
    },
    persistSettings: () => { /* no op */
    },
    isLoadingCalendarPreferences: true,
    isErrorCalendarPreferences: false,
    refetchCalendarPreferences: () => { /* no op */ },
    calendarPreferences: {} as unknown as CalendarPreferences,
});

const SettingsContextProvider = ({children}: { children: React.ReactNode }) => {
    const {localStorage} = useContext(StorageContext);
    const [settings, setSettings] = useState(defaultSettings);
    const auth = useAuth();

    const { isLoading: isLoadingCalendarPreferences, isError: isErrorCalendarPreferences, data: calendarPreferences, refetch: refetchCalendarPreferences } = useApiQuery<CalendarPreferences>({
        endpoint: process.env.REACT_APP_API_V1,
        path: `org/organisation/${auth?.userData?.profile?.sub}/calendar`,
        body: {}
    } as ApiFetchParams, "calendar");

    useEffect(() => setSettings((prevSettings) => {
        const storedSettings: any = {...prevSettings};
        Object.getOwnPropertyNames(prevSettings).forEach((key) => {
            storedSettings[key] = localStorage.getItem(`settings.${key}`) || storedSettings[key];
        });
        return storedSettings;
    }), [localStorage]);

    const setSetting = useCallback((key: string, value: string) => setSettings((prevSettings) => {
        const storedSettings: any = {...prevSettings};
        storedSettings[key] = value;
        localStorage.setItem(`settings.${key}`, value);
        return storedSettings;
    }), [localStorage]);

    const persistSettings = useCallback(() => {
        Object.getOwnPropertyNames(settings).forEach((key) => {
            localStorage.setItem(`settings.${key}`, settings[key]);
        });
    }, [settings]);

    const context = useMemo(() => ({settings, setSetting, persistSettings, isLoadingCalendarPreferences, isErrorCalendarPreferences, refetchCalendarPreferences, calendarPreferences: calendarPreferences || {daysStart: DEFAULT_BUSINESS_START_HOUR, weeksStart: "monday"}}), [settings, setSetting, persistSettings, refetchCalendarPreferences, isErrorCalendarPreferences, isLoadingCalendarPreferences, calendarPreferences]);
    return <SettingsContext.Provider value={context}>{children}</SettingsContext.Provider>;
}

export {
    SettingsContext,
    SettingsContextProvider
};
